import { useState, useMemo, forwardRef } from 'react'
import styles from '../DavidPifView.module.css'
import classNames from 'classnames'
import { motion, AnimatePresence } from 'framer-motion'
import { Faq } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'

interface FaqItemProps {
  question: string
  answer: string | React.ReactNode
  isOpen: boolean
  onToggle: () => void
}

function FaqItem({ question, answer, isOpen, onToggle }: FaqItemProps) {
  const [rotation, setRotation] = useState(0)

  return (
    <div
      className="relative flex w-full shrink-0 cursor-pointer content-stretch items-start gap-[8px]"
      onClick={onToggle}
    >
      <div className="relative flex shrink-0 flex-col content-stretch items-start">
        <motion.div
          className="relative size-[24px] shrink-0"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{
            duration: 0.6,
            ease: 'easeInOut',
          }}
          onUpdate={(latest) => {
            if (typeof latest.rotate === 'number') {
              setRotation(latest.rotate)
            }
          }}
        >
          {/* Plus icon */}
          <motion.div
            className="absolute inset-0"
            animate={{
              opacity: rotation < 90 ? 1 : 0,
            }}
            transition={{ duration: 0.125 }}
          >
            <img
              src="https://images.angelstudios.com/image/upload/v1760914894/theatrical/david/icon-plus.svg"
              alt=""
              className="block size-full"
            />
          </motion.div>
          {/* Minus icon */}
          <motion.div
            className="absolute inset-0"
            animate={{
              opacity: rotation >= 90 ? 1 : 0,
            }}
            transition={{ duration: 0.125 }}
          >
            <img
              src="https://images.angelstudios.com/image/upload/v1760914894/theatrical/david/icon-minus.svg"
              alt=""
              className="block size-full"
            />
          </motion.div>
        </motion.div>
      </div>
      <div className="relative flex min-h-px min-w-px shrink-0 grow basis-0 flex-col content-stretch items-start justify-center gap-[8px]">
        <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[8px]">
          <div className="font-whitney-bold relative flex w-full shrink-0 flex-col justify-center text-[20px] not-italic leading-[0] text-white">
            <p className="font-bold leading-[26px]">{question}</p>
          </div>
        </div>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              className="relative flex w-full shrink-0 flex-col content-stretch items-center justify-center gap-[8px] overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <div className="font-whitney-medium relative flex w-full shrink-0 flex-col justify-center text-[0px] not-italic leading-[0] text-white">
                <p className="text-[16px] leading-[28px]">{answer}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

function toPlainText(html: string): string {
  if (!html) return ''
  // Prefer browser-based parsing when available
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }
  // SSR-safe fallback: strip tags and minimally decode common entities
  const text = html.replace(/<[^>]*>/g, ' ')
  return text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim()
}

export const FaqSection = forwardRef<HTMLElement, { faqs?: Faq[] }>(({ faqs }, ref) => {
  const { t } = useTranslate('pif')
  const [openIndices, setOpenIndices] = useState<number[]>([0])
  const items = useMemo(
    () =>
      (faqs ?? []).map((f) => ({
        question: toPlainText((f.question as string) ?? ''),
        answer: toPlainText((f.answer as string) ?? ''),
      })),
    [faqs],
  )

  const toggleFaq = (index: number) => {
    setOpenIndices((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]))
  }

  if (!items || items.length === 0) return null

  return (
    <section ref={ref} className={classNames('relative w-full bg-[#141447]', styles['section-md'])}>
      <div className="relative mx-auto flex w-full max-w-[1440px] flex-col items-start gap-[40px]">
        <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[12px] not-italic text-white">
          <p className="font-angel-royal relative w-full text-5xl lowercase not-italic leading-[2.5rem] text-white md:text-[3.5rem] md:leading-[3rem]">
            {t('frequentlyAskedQuestions', 'Frequently Asked Questions')}
          </p>
          <p className="font-whitney-medium md:font-whitney-medium relative w-full shrink-0 text-[16px] not-italic leading-[24px] text-white md:text-[18px] md:leading-[28px]">
            {t(
              'davidFaqDescription',
              'Review this collection of frequently asked questions to help answer anything that you might be wondering.',
            )}
          </p>
        </div>
        <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[32px]">
          {items.map((faq, index) => (
            <FaqItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndices.includes(index)}
              onToggle={() => toggleFaq(index)}
            />
          ))}
        </div>
      </div>
    </section>
  )
})

FaqSection.displayName = 'FaqSection'
